import React, { useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { v4 as uuid } from 'uuid'
import { useQuery } from '@tanstack/react-query'
import { MarketplaceList } from '../../../../components/society/MarketplaceList'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { IconChoices } from '../../../../components/common/Icon'
import { ButtonVariant } from '../../../../components/common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
// eslint-disable-next-line
import { CreateUpdateMarketplaceItemModal } from '../../../../components/society/MarketplaceItem/CreateUpdateMarketplaceItemModal'
import { useModal } from '../../../../hooks/useModal'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ViewBase } from '../../../../components/common/ViewBase'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { ButtonPropsWithId, ErrorView } from '../../../error/ErrorView'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { useUserBlockedInSociety } from '../../../../hooks/useUserBlockedInSociety'
import { BlockedCallout } from '../../../../components/blocked/BlockedCallout'
import { useAuth } from '../../../../hooks/useAuth'
import { unitQueries } from '../../../../api/units'
import { resourceQueries } from '../../../../api/resources'
import { i18nSocietyType } from '../../../../i18n'

export const MarketplaceListView = (): React.ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { userId } = useAuth()
  const { society, isLoading: isSocietyLoading } = useCurrentSociety()
  const userBlockedInSociety = useUserBlockedInSociety(society?._id)
  useDocumentTitle(
    reverseDocumentTitle('society:marketplace', {
      '{{ societyName }}': society?.name,
    })
  )
  const { translate } = useAppTranslation()
  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const { data: resources, isLoading: isResourcesLoading } = useQuery(
    resourceQueries.getResourcesPerSociety(society?._id)
  )

  const { data: units, isLoading: isUnitsLoading } = useQuery(
    unitQueries.getUserUnits(userId)
  )
  const userSocietyUnits = units?.filter(
    (unit) => unit.societyId === society?._id
  )

  const getFilteredResources = useCallback(() => {
    const options = {
      threshold: 0.3,
      keys: ['title', 'description'],
    }
    const _resources = resources || []

    const fuse = new Fuse(_resources, options)

    const filteredResources =
      searchString === ''
        ? _resources
        : fuse.search(searchString).map((result) => result.item)

    return filteredResources
  }, [resources, searchString])

  const filteredResources = getFilteredResources()

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const dataIsBeingFetched =
    isResourcesLoading || isUnitsLoading || isSocietyLoading
  const noListings = filteredResources.length === 0

  const buttons =
    !userBlockedInSociety &&
    !noListings &&
    userSocietyUnits &&
    userSocietyUnits.length > 0
      ? [
          {
            label: translate('marketplaceListView.buttons.createListing'),

            variant: ButtonVariant.PRIMARY,
            icon: IconChoices.PLUS_SIGN,

            onClick: openCreateModal,
          },
        ]
      : []

  const errorViewButtons: ButtonPropsWithId[] | undefined =
    userSocietyUnits && userSocietyUnits.length > 0
      ? ([
          {
            id: uuid(),
            button: {
              label: translate('marketplaceListView.buttons.createListing'),
              variant: ButtonVariant.PRIMARY,
              icon: IconChoices.PLUS_SIGN,
              onClick: openCreateModal,
            },
          },
        ] as ButtonPropsWithId[])
      : undefined

  const errorView: ErrorView | undefined =
    noListings && searchString === ''
      ? {
          title: translate('marketplaceListView.emptyState.title'),
          subtitle:
            userSocietyUnits && userSocietyUnits.length > 0
              ? translate('marketplaceListView.emptyState.subtitle')
              : translate(
                  'marketplaceListView.emptyState.subtitleMissingUnit',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                ),
          buttons: errorViewButtons,
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  const noListingsFoundView: ErrorView | undefined =
    noListings && searchString !== ''
      ? {
          title: translate('marketplaceListView.noListingFound.title'),
          subtitle: translate('marketplaceListView.noListingFound.subtitle'),
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('marketplaceListView.title')}
        subtitle={translate('marketplaceListView.subtitle')}
        buttons={buttons}
        searchBar={{ value: searchString, onChange: onChangeSearchString }}
        loading={dataIsBeingFetched}
        errorView={errorView || noListingsFoundView}
      >
        {society && userBlockedInSociety && (
          <BlockedCallout selectedSocietyId={society._id} className="mb-4" />
        )}
        <MarketplaceList resources={filteredResources} />
      </ViewBase>
      <CreateUpdateMarketplaceItemModal
        show={showCreateModal}
        close={closeCreateModal}
      />
    </ErrorBoundary>
  )
}
